import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import cs from 'classnames';
import Spinner from '../../Spinner';
import Button from '../../form/Button';
import SubPaymentMethodModal from './Modal';

import { get } from '../../../utils/lodash';
import { translate } from '../../Translate';
import { getImagePath, shouldApplyCouponToShoppingCart } from '../../../utils';
import { useStripe } from '../../../utils/hooks/useScript';
import { getFieldOptions, delayShow, documentValidationsPerCountry } from './utils';
import { ModalPaymentMethodsType } from '../../../enums';

const CardForm = ({
  config,
  paymentConfig,
  history,
  userData,
  handleSubmit,
  intl,
  client,
  transformEntities,
  deviceCountry,
  couponApply,
  modalType,
  goBack,
  currentSubPaymentMethod,
  currentPaymentMethod,
  shoppingCart,
  countryCode,
  searchParams,
  subscriberConfig,
  ...props
}) => {
  const [isSubmitting, setSubmitting] = useState(false);
  const [stripeFieldsValid, setStripeFieldsValid] = useState({
    cardNumber: false,
    cardExpiry: false,
    cardCvc: false
  });
  const [fieldsError, setFieldsError] = useState({
    name: {
      show: false,
      error: translate(intl, 'Card.validations.required'),
      isValid: false
    },
    ...(countryCode?.toUpperCase() === 'BR' && {
      userDocument: {
        show: false,
        error: translate(intl, 'Card.validations.required'),
        isValid: false
      }
     })
  });
  const [modal, setModal] = useState(null);
  const [pristine, setPristine] = useState(false);
  const [stripeFields, setStripeFields] = useState({});
  const [loaded] = useStripe(paymentConfig);
  const clientCountries = config.IDPs.find(c => c.shortCode === client)
  const [country, setCountry] = useState(undefined);

  useEffect(() => {
    const countryFind = clientCountries.countries.find((c) => c.code === userData.country)
    if (countryFind) {
      setCountry(userData.country);
    } else {
      setCountry(clientCountries.countries[0]);
    }
  }, []);

  const handleCloseModal = () => setModal(null);
  const handleRedirect = () => {
    const cloudpayConfig = window.AppENV.cloudpay;
    const url = `${cloudpayConfig.baseURL}/redirect/goBackToReturnUrl${window.location.search}&jwt=${localStorage.getItem('jwt')}`
    window.location.href = url;
  };

  // Mounting stripe components
  useEffect(() => {
    const apiKey = get(paymentConfig, 'data.merchantAuthentication.apiKey', '');
    const defaultOptions = 'default';
    const smartFieldsOptions = getFieldOptions(intl, translate)[client] || getFieldOptions(intl, translate)[defaultOptions];

    if (loaded) {
      const stripe = window.Stripe(apiKey);
      const elements = stripe.elements();

      const cardNumberElement = elements.create('cardNumber', smartFieldsOptions.pan);
      const cardExpiryElement = elements.create('cardExpiry', smartFieldsOptions.expiration);
      const cardCvcElement = elements.create('cardCvc', smartFieldsOptions.cvv);

      // Mounting elements
      cardNumberElement.mount(document.getElementById('pan'));
      cardExpiryElement.mount(document.getElementById('expiration'));
      cardCvcElement.mount(document.getElementById('cvv'));

      cardNumberElement.on('ready', delayShow('pan', 700));
      cardExpiryElement.on('ready', delayShow('expiration', 700));
      cardCvcElement.on('ready', delayShow('cvv', 700));

      cardNumberElement.on('change', (event) => {
        setStripeFieldsValid(prev => ({
          ...prev,
          cardNumber: event.complete && !event.error
        }));
      });

      cardExpiryElement.on('change', (event) => {
        setStripeFieldsValid(prev => ({
          ...prev,
          cardExpiry: event.complete && !event.error
        }));
      });

      cardCvcElement.on('change', (event) => {
        setStripeFieldsValid(prev => ({
          ...prev,
          cardCvc: event.complete && !event.error
        }));
      });

      setStripeFields({
        stripeInstance: stripe,
        elements,
        card: cardNumberElement
       });
    }
  }, [loaded]);

  useEffect(() => {
    // validate fields and enable forms
    const isValidForm = Object.values(fieldsError).every(field => field.isValid);
    const isValidStripeFields = Object.values(stripeFieldsValid).every(field => field);
    setPristine(isValidForm && isValidStripeFields);
  }, [fieldsError, stripeFieldsValid]);

  const handleFormSubmition = async (stripeInstance, card) => {
    const description = get(document.getElementById('description'), 'value');
    const name = get(document.getElementById('name'), 'value');
    const userDocument = get(document.getElementById('userDocument'), 'value');
    const isDirectFlow = currentSubPaymentMethod.allowed_flows.includes('DIRECT');
    const type = currentSubPaymentMethod.type;

    const submitParams = {
      description,
      country,
      forceCountry: country,
      ...(searchParams.promoSlug && { promoSlug: searchParams.promoSlug }),
      ...(userDocument && { userDocument }),
      name,
      paymentMethodId: currentSubPaymentMethod.id,
      paymentMethodType: type,
      paymentMethodFlow: isDirectFlow ? 'DIRECT' : 'REDIRECT',
    };
    if (shouldApplyCouponToShoppingCart(couponApply, shoppingCart)) {
      submitParams.couponCode = couponApply.code;
    }
    setSubmitting(true);

    const { error, paymentMethod } = await stripeInstance.createPaymentMethod({
      type: 'card',
      card,
      billing_details: {
        name,
      },
    });

    if (error) {
      setModal({
        code: 300,
        error: true,
        img: getImagePath('ic_rejected.svg'),
        default: 'error',
        message: error.message,
        handleClick: handleCloseModal
      });
      setTimeout(() => {
        setSubmitting(false);
      }, 2000); // wait 2 seconds to submit again
      return;
    }
    // Set payment id in token attribute
    paymentMethod?.id && (submitParams.token = paymentMethod.id);
    // Call handleSubmit to do purchase or change plan
    handleSubmit(paymentMethod, submitParams).then(response => {
      setSubmitting(false);
      setModal({
        code: 200,
        img: getImagePath('ic_approved.svg', client),
        transaction: response,
        forceToChangeToFavorite: props.forceToChangeToFavorite,
        type: modalType === ModalPaymentMethodsType.NEW_PAYMENT_METHOD ? modalType : 'default',
        default: 'default',
        handleClick: props.handleRedirect || handleRedirect,
        handleCloseModal: () => {
          transformEntities({
            subscriber: {
              subscription: {
                ignoreRedirect: false
              }
            }
          });

          history.replace(`/${client}/subscribe/subscriptions/management${history.location.search}`);
        }
      });

    }).catch(err => {
      setModal({
        code: 'rejected',
        error: true,
        img: getImagePath('ic_rejected.svg'),
        default: 'error',
        ...(err?.body?.message && { message: err?.body?.message }),
        handleClick: handleCloseModal
      });
    }).finally(() => {
      setSubmitting(false);
    });
  };
  useEffect(() => {
    const { elements, stripeInstance, card } = stripeFields;
    if (elements) {
      document.getElementById('fields-form-card').onsubmit = async function (e) {
        e.preventDefault();
        const isSameSubPaymenthMethod = currentSubPaymentMethod?.paymentMethod === subscriberConfig?.subscription?.paymentMethod;
        const isNewPaymentMethodRoute = (history?.location?.pathname || '').includes('/subscribe/subscriptions/new-payment-method');
        const forceFavoriteMethod = history?.location?.state?.defaultPaymentChange || props.forceToChangeToFavorite;
        const shouldOpenFavoriteModal = isSameSubPaymenthMethod && forceFavoriteMethod;
        if (document.getElementById('fields-form-card').checkValidity() && isNewPaymentMethodRoute && shouldOpenFavoriteModal) {
          setModal({
            type: ModalPaymentMethodsType.FORCE_TO_CHANGE_FAVORITE,
            modalType: 'default',
            handlePrimaryClick: () => {
              props.setShouldChangeFavoritePaymentMethod(false);
              handleFormSubmition(stripeInstance, card);
              setModal(false);
            },
            handleClick: () => {
              props.setShouldChangeFavoritePaymentMethod(true);
              handleFormSubmition(stripeInstance, card);
              setModal(false);
            }
          });
        } else {
          handleFormSubmition(stripeInstance, card);
        }
      }
    }
  }, [stripeFields, country, couponApply]);

  const hasUserDocumentErrors = useCallback(({ userDocument }) => {
    if (userDocument.length > 0) {
      const validations = documentValidationsPerCountry[country];
      const valids = validations.map((validation) => {
        let isValid = true;
        let errorId;
        if (validation.min && userDocument.length < validation.min) {
          isValid = false;
          errorId = 'min';
        }
        if (validation.max && userDocument.length > validation.max) {
          isValid = false;
          errorId = 'max';
        }
        if (validation.length && userDocument.length !== validation.length) {
          isValid = false;
          errorId = 'length';
        }
        if (validation.digits && !userDocument.match(/^\d+$/)) {
          isValid = false;
          errorId = 'digits';
        }
        return { valid: isValid, errorId };
      });
      const isSomeoneValid = valids.some((validation) => validation.valid);
      const result = isSomeoneValid
        ? { show: false, error: undefined, isValid: true }
        : {
          show: true,
          error: get(valids.find((validation) => !validation.value), 'errorId'),
          isValid: false
        };
      setFieldsError({ ...fieldsError, userDocument: result });
      return result;
    }
    return { show: true, error: { isRequired: true }, isValid: false };
  }, [country]);

  useEffect(() => {
    if (
      country &&
      document.getElementById('userDocument') &&
      document.getElementById('userDocument').value) {
      hasUserDocumentErrors({ userDocument: document.getElementById('userDocument').value })
    }
  }, [country]);

  if (!loaded) {
    return <Spinner mauto />;
  }

  return (
    <React.Fragment>
      <SubPaymentMethodModal entity="card" intl={intl} modal={modal} />

      <section className="row example example-1 submitting payment-form">
        <form id="fields-form-card">
          <div className="fieldset">
            <fieldset>
              <div className='field'>
                <div id="pan"></div>
              </div>
              <div className={cs('field', {
                'focus-error': fieldsError.name.show && fieldsError.name.error,
              })}
              >
                <input
                  id="name"
                  className="input"
                  type="text"
                  onBlur={
                    e => setFieldsError({
                      ...fieldsError,
                      name: {
                        show: true,
                        error: e.target.value ? undefined : translate(intl, 'Card.validations.required'),
                        isValid: !!(e.target.value)
                      }
                    })
                  }
                  onChange={
                    e => setFieldsError({
                      ...fieldsError,
                      name: {
                        show: true,
                        error: e.target.value ? undefined : translate(intl, 'Card.validations.required'),
                        isValid: !!(e.target.value)
                      }
                    })
                  }
                  placeholder={translate(intl, 'Card.placeholder.name')}
                  autoComplete="off"
                />
              </div>
              <div className="field-error">
                {fieldsError.name.show && fieldsError.name.error && (
                  <FormattedMessage
                    id="Card.validations.required"
                  />
                )}
              </div>

              <div className="container p-0 m-0">
                <div className="container-column">
                  <div className='field half'>
                    <div id="expiration"></div>
                  </div>
                </div>
                <div className="container-column">
                  <div className='field half cvv'>
                    <div id="cvv"></div>
                  </div>
                </div>
              </div>
              {
                countryCode?.toUpperCase() === 'BR' &&
                <>
                  <div className={cs('field', {
                    'focus-error': fieldsError.userDocument.show && fieldsError.userDocument.error,
                  })}
                  >
                    <input
                      id="userDocument"
                      className="input"
                      type="text"
                      onBlur={(e) => setFieldsError(
                        {
                          ...fieldsError,
                          userDocument: hasUserDocumentErrors({ userDocument: e.target.value })
                        })}
                      onChange={(e) => setFieldsError(
                        {
                          ...fieldsError,
                          userDocument: hasUserDocumentErrors({ userDocument: e.target.value })
                        })}
                      placeholder={country ? translate(intl, `Form.placeholder.document.${country}`) : 'ID'}
                      autoComplete="off" />
                  </div><div className="field-error">
                      {get(fieldsError, 'userDocument').show && get(fieldsError, 'userDocument').error
                        ? get(fieldsError, 'userDocument').error.isRequired
                          ? (
                            <FormattedMessage
                              id="Card.validations.required" />
                          ) : (
                            <FormattedMessage
                              id={`Form.validations.${country}.${get(fieldsError, 'userDocument').error}`} />
                          )
                        : null}
                    </div>
                  </>
              }
            </fieldset>

            <div className="buttons">
              <Button
                type="submit"
                className={cs({ loading: isSubmitting })}
                disabled={
                  isSubmitting ||
                  !pristine
                }
              >
                <FormattedMessage id={
                  modalType === ModalPaymentMethodsType.NEW_PAYMENT_METHOD
                    ? 'Form.label.addPaymentMethod'
                    : 'Form.label.submit'
                }
                />
              </Button>

              {goBack && (
                <Button
                  type="button"
                  className="empty"
                  onClick={goBack}
                  disabled={isSubmitting}
                >
                  <FormattedMessage id="Form.label.cancel" />
                </Button>
              )}
            </div>
          </div>
        </form>
      </section>
    </React.Fragment>
  );
};

CardForm.propTypes = {
  client: PropTypes.string.isRequired,
  paymentConfig: PropTypes.object.isRequired,
  config: PropTypes.shape({
    sandbox: PropTypes.bool,
    IDPs: PropTypes.array,
    shortCode: PropTypes.string,
    products: PropTypes.array,
  }).isRequired,
  handleSubmit: PropTypes.func.isRequired,
  transformEntities: PropTypes.func.isRequired,
  forceToChangeToFavorite: PropTypes.bool,
  intl: PropTypes.object.isRequired,
  deviceCountry: PropTypes.string,
  couponApply: PropTypes.any,
  countryCode: PropTypes.string.isRequired,
  goBack: PropTypes.func,
  currentSubPaymentMethod: PropTypes.shape({
    paymentMethod: PropTypes.string.isRequired,
    id: PropTypes.string.isRequired,
    type: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    logo: PropTypes.string,
    allowed_flows: PropTypes.arrayOf(PropTypes.string).isRequired
  }),
  userData: PropTypes.shape({
    country: PropTypes.string,
    country_code: PropTypes.string,
    user_token: PropTypes.string
  }),
  currentPaymentMethod: PropTypes.string.isRequired,
  history: PropTypes.shape({
    push: PropTypes.func,
    replace: PropTypes.func,
    goBack: PropTypes.func,
    location: PropTypes.shape({
      search: PropTypes.string
    })
  }).isRequired,
  shoppingCart: PropTypes.object,
  modalType: PropTypes.string,
  handleRedirect: PropTypes.func
};
export default CardForm;